import { useEffect, useState, type ComponentType } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Eye, FileText, Flag, Heart, PlusCircle, Send } from 'lucide-react';
import { fetchShowingNow, useContentStore } from '@/store/contentStore';
import { CARD_TYPE_LABELS, sourceContentOf, type CardStatus, type CardType, type ContentCard } from '@/types/content';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, Skeleton } from '@/components/ui/misc';
import { CardTypeBadge, StatusBadge } from '@/components/badges';

export function Dashboard() {
  const { cards, nextToken, loading, error, fetchCards, stopPollingTranslations, stopPollingGeneration } = useContentStore();

  useEffect(() => {
    // Always the unfiltered list: the Content page stores a date filter in the same store
    fetchCards({});
  }, [fetchCards]);

  // fetchCards starts the translation/generation pollers; stop them when this page unmounts
  useEffect(() => {
    return () => {
      stopPollingTranslations();
      stopPollingGeneration();
    };
  }, [stopPollingTranslations, stopPollingGeneration]);

  // GET /content returns one page of at most 100 cards. The numbers below describe
  // that page, so they say "100+" when the API reports another page.
  const isPartial = nextToken !== null;
  const partial = (n: number | string) => (isPartial ? `${n}+` : n);

  // "Showing now": the same list the app shows today, from the backend view (VAP-823)
  const [showingNow, setShowingNow] = useState<ContentCard[] | null>(null);
  const [showingNowError, setShowingNowError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchShowingNow()
      .then(list => { if (!cancelled) setShowingNow(list); })
      .catch(err => { if (!cancelled) setShowingNowError(err instanceof Error ? err.message : 'Failed to load'); });
    return () => { cancelled = true; };
  }, []);

  const totalCards = cards.length;
  const published = cards.filter(c => c.status === 'PUBLISHED').length;
  const drafts = cards.filter(c => c.status === 'DRAFT').length;
  const totalFetches = cards.reduce((sum, card) => sum + card.metrics.fetchCount, 0);
  const totalLikes = cards.reduce((sum, card) => sum + card.metrics.likeCount, 0);

  const pushFlaggedCards = cards.filter(card => card.isPushFlagged);
  const recentCards = [...cards]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 6);

  const cardsByType = cards.reduce<Record<string, number>>((acc, card) => {
    acc[card.cardType] = (acc[card.cardType] || 0) + 1;
    return acc;
  }, {});
  const typeRows = Object.entries(cardsByType).sort(([, a], [, b]) => b - a);

  const isInitialLoad = loading && cards.length === 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Overview of your engagement content"
        actions={
          <Button asChild>
            <Link to="/content/create">
              <PlusCircle />
              Create Content
            </Link>
          </Button>
        }
      />

      {error && <Alert variant="error" title="Error loading data">{error}</Alert>}

      {/* Stat cards link to the filtered Content list */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={FileText} label={isPartial ? 'Cards (latest 100)' : 'Total cards'} value={partial(totalCards)} to="/content?date=all" tone="primary" loading={isInitialLoad} />
        <StatCard icon={Send} label="Published" value={partial(published)} hint={isPartial ? 'in the latest 100' : undefined} to={listLink('PUBLISHED')} tone="success" loading={isInitialLoad} />
        <StatCard icon={FileText} label="Drafts" value={partial(drafts)} hint={isPartial ? 'in the latest 100' : undefined} to={listLink('DRAFT')} tone="warning" loading={isInitialLoad} />
        <StatCard icon={Heart} label="Likes" value={partial(totalLikes.toLocaleString())} hint={`${totalFetches.toLocaleString()}${isPartial ? '+' : ''} fetches`} to="/content?date=all" tone="accent" loading={isInitialLoad} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-primary" aria-hidden />
              <CardTitle>Showing now</CardTitle>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/content">
                Open
                <ArrowUpRight />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {showingNowError ? (
              <EmptyLine>{showingNowError}</EmptyLine>
            ) : showingNow === null ? (
              <ListSkeleton rows={3} />
            ) : showingNow.length > 0 ? (
              <ul className="divide-y divide-border">
                {showingNow.map(card => <CardRow key={card.cardId} card={card} showDate />)}
              </ul>
            ) : (
              <EmptyLine>The app shows no cards today.</EmptyLine>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <Flag className="h-4 w-4 text-accent" aria-hidden />
              <CardTitle>Push flagged</CardTitle>
            </div>
            <CardDescription>{pushFlaggedCards.length} total</CardDescription>
          </CardHeader>
          <CardContent>
            {isInitialLoad ? (
              <ListSkeleton rows={3} />
            ) : pushFlaggedCards.length > 0 ? (
              <ul className="divide-y divide-border">
                {pushFlaggedCards.slice(0, 4).map(card => <CardRow key={card.cardId} card={card} showDate />)}
              </ul>
            ) : (
              <EmptyLine>No push flagged content.</EmptyLine>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Content by type</CardTitle>
            <CardDescription>{isPartial ? 'Latest 100 cards. Click a type to open its list.' : 'Click a type to open its list.'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {isInitialLoad ? (
              <ListSkeleton rows={4} />
            ) : typeRows.length === 0 ? (
              <EmptyLine>No content yet.</EmptyLine>
            ) : (
              typeRows.map(([type, count]) => (
                <Link
                  key={type}
                  to={`/content?date=all&type=${type}`}
                  className="group flex items-center gap-3 rounded-md -mx-2 px-2 py-1.5 transition-colors hover:bg-muted"
                >
                  <span className="w-32 shrink-0 truncate text-sm text-foreground group-hover:text-primary">
                    {CARD_TYPE_LABELS[type as CardType] || type}
                  </span>
                  <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                    <span className="block h-full rounded-full bg-primary" style={{ width: `${(count / totalCards) * 100}%` }} />
                  </span>
                  <span className="w-8 text-right text-sm font-medium tabular-nums">{count}</span>
                </Link>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>Recent content</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/content?date=all">
                All content
                <ArrowUpRight />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {isInitialLoad ? (
              <ListSkeleton rows={5} />
            ) : (
              <ul className="divide-y divide-border">
                {recentCards.map(card => <CardRow key={card.cardId} card={card} showDate showMetrics />)}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function listLink(status: CardStatus): string {
  return `/content?date=all&status=${status}`;
}

interface StatCardProps {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  hint?: string;
  to: string;
  tone: 'primary' | 'success' | 'warning' | 'accent';
  loading: boolean;
}

const TONE_CLASSES: Record<StatCardProps['tone'], string> = {
  primary: 'bg-primary-50 text-primary dark:bg-primary-900/40',
  success: 'bg-success/15 text-success',
  warning: 'bg-warning/15 text-warning',
  accent: 'bg-accent-50 text-accent dark:bg-accent-900/40',
};

function StatCard({ icon: Icon, label, value, hint, to, tone, loading }: StatCardProps) {
  return (
    <Link to={to} className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg">
      <Card className="h-full transition-shadow group-hover:shadow-card-hover">
        <CardContent className="flex items-start gap-4 p-5">
          <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', TONE_CLASSES[tone])}>
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-muted-foreground">{label}</p>
            {loading ? (
              <Skeleton className="mt-1 h-7 w-16" />
            ) : (
              <p className="text-2xl font-semibold tabular-nums tracking-tight">{value}</p>
            )}
            {hint && !loading ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
          </div>
          <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
        </CardContent>
      </Card>
    </Link>
  );
}

function CardRow({ card, showDate, showMetrics }: { card: ContentCard; showDate?: boolean; showMetrics?: boolean }) {
  return (
    <li>
      <Link
        to={`/content/${card.cardId}`}
        className="-mx-2 flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-muted"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{sourceContentOf(card)?.title || 'Untitled'}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <CardTypeBadge cardType={card.cardType} />
            <StatusBadge status={card.status} />
            {showDate ? <span className="text-xs text-muted-foreground">{card.publishDate}</span> : null}
          </div>
        </div>
        {showMetrics ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
            <Eye className="h-3.5 w-3.5" aria-hidden />
            {card.metrics.fetchCount}
          </span>
        ) : null}
      </Link>
    </li>
  );
}

function EmptyLine({ children }: { children: string }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}
